import React, { useEffect, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { Box, useTheme } from '@mui/material';
import { Layout } from '@core/components/layout';
import { useBrand } from '@core/contexts/BrandContext';
import type {
  LayoutExpandedLogo,
  WebLayoutVariant,
} from '@core/contexts/LayoutContext';
import { NavItem } from '@core/interfaces';
import { readLocalItem, writeLocalItem } from '@core/utils/localStorage';
import OrderDialog from './OrderDialog';
import UsernameFlashDialog from './UsernameFlashDialog';

const LOGO_HEIGHT = 100;
const EXPANDED_LOGO_WIDTH = 320;
const EXPANDED_LOGO_LEFT_OFFSET = -12;
const WEB_VARIANT_LOCAL_STORAGE_KEY = 'lv.webVariant.v2';

interface LeitourgosLayoutProps {
  navItems: NavItem[];
}

const LeitourgosExpandedLogo: LayoutExpandedLogo['component'] = ({
  sidebarOpen,
  logoHeight,
}) => {
  const theme = useTheme();
  const { logoHorizontal } = useBrand();
  return (
    <Box
      component={RouterLink}
      to="/dashboard"
      aria-label="Go to dashboard"
      sx={{
        position: 'fixed',
        top: 0,
        left: sidebarOpen ? EXPANDED_LOGO_LEFT_OFFSET : 0,
        width: sidebarOpen ? EXPANDED_LOGO_WIDTH : LOGO_HEIGHT,
        height: logoHeight,
        opacity: sidebarOpen ? 1 : 0,
        zIndex: (t) => t.zIndex.drawer + 1,
        pointerEvents: sidebarOpen ? 'auto' : 'none',
        cursor: 'pointer',
        display: 'block',
        backgroundColor: 'primary.main',
        maskImage: `url(${logoHorizontal})`,
        maskRepeat: 'no-repeat',
        maskSize: 'contain',
        maskPosition: 'center',
        WebkitMaskImage: `url(${logoHorizontal})`,
        WebkitMaskRepeat: 'no-repeat',
        WebkitMaskSize: 'contain',
        WebkitMaskPosition: 'center',
        transition: theme.transitions.create(['width', 'left', 'opacity'], {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.enteringScreen,
        }),
      }}
    />
  );
};

const LeitourgosLayout: React.FC<LeitourgosLayoutProps> = ({ navItems }) => {
  const navigate = useNavigate();
  const [createOrderOpen, setCreateOrderOpen] = useState(false);
  const [webVariant, setWebVariant] = useState<WebLayoutVariant>(() => {
    const stored = readLocalItem(WEB_VARIANT_LOCAL_STORAGE_KEY);
    return stored === 'webone' || stored === 'webtwo' ? stored : 'webtwo';
  });

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (!(event.metaKey || event.ctrlKey) || event.altKey || event.shiftKey) {
        return;
      }
      const key = event.key.toLowerCase();
      if (key === 'l') {
        event.preventDefault();
        setWebVariant((prev) => {
          const next: WebLayoutVariant =
            prev === 'webone' ? 'webtwo' : 'webone';
          writeLocalItem(WEB_VARIANT_LOCAL_STORAGE_KEY, next);
          return next;
        });
        return;
      }
      if (key === 'o') {
        event.preventDefault();
        setCreateOrderOpen(true);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  return (
    <>
      <UsernameFlashDialog />
      <OrderDialog
        open={createOrderOpen}
        onClose={() => setCreateOrderOpen(false)}
        onSuccess={(order) => {
          setCreateOrderOpen(false);
          navigate(`/orders/${order.id}`);
        }}
      />
      <Layout
        navItems={navItems}
        webVariant={webVariant}
        showBreadcrumbs={false}
        expandedLogo={{
          logoHeight: LOGO_HEIGHT,
          logoWidth: EXPANDED_LOGO_WIDTH + EXPANDED_LOGO_LEFT_OFFSET * 2,
          component: LeitourgosExpandedLogo,
        }}
      />
    </>
  );
};

export default LeitourgosLayout;
